import { useEffect, useRef, useState, type KeyboardEvent, type ClipboardEvent } from "react";
import { motion } from "framer-motion";

import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  autoFocus?: boolean;
  error?: boolean;
  disabled?: boolean;
}

export function OtpInput({
  length = 5,
  value,
  onChange,
  onComplete,
  autoFocus = true,
  error = false,
  disabled = false,
}: OtpInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const completedRef = useRef<string | null>(null);
  const [focused, setFocused] = useState<number | null>(null);

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (value.length === length && onComplete && completedRef.current !== value) {
      completedRef.current = value;
      onComplete(value);
    }
    if (value.length < length) completedRef.current = null;
  }, [value, length, onComplete]);

  const digits = Array.from({ length }, (_, i) => value[i] ?? "");

  const setDigit = (i: number, d: string) => {
    const cleaned = d.replace(/\D/g, "").slice(0, 1);
    const next = digits.slice();
    next[i] = cleaned;
    const joined = next.join("");
    onChange(joined);
    if (cleaned && i < length - 1) {
      inputsRef.current[i + 1]?.focus();
      haptic.select();
    }
  };

  const handleKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = digits.slice();
        next[i] = "";
        onChange(next.join(""));
      } else if (i > 0) {
        const next = digits.slice();
        next[i - 1] = "";
        onChange(next.join(""));
        inputsRef.current[i - 1]?.focus();
      }
      haptic.select();
    } else if (e.key === "ArrowLeft" && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < length - 1) {
      inputsRef.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    const target = Math.min(pasted.length, length - 1);
    setTimeout(() => inputsRef.current[target]?.focus(), 0);
    haptic.impact("light");
  };

  return (
    <div className="flex gap-2.5 justify-center">
      {digits.map((d, i) => {
        const isActive = focused === i;
        const isFilled = !!d;
        return (
          <div key={i} className="relative">
            <motion.div
              animate={{
                scale: isActive ? 1.04 : 1,
              }}
              transition={{ type: "spring", stiffness: 380, damping: 28 }}
              className={cn(
                "relative h-14 w-12 rounded-2xl",
                "bg-surface border transition-colors",
                error
                  ? "border-danger/60 shadow-[0_0_0_4px_rgba(255,77,109,0.12)]"
                  : isActive
                    ? "border-accent shadow-[0_0_0_4px_rgba(255,107,53,0.15)]"
                    : isFilled
                      ? "border-white/[0.14]"
                      : "border-white/[0.08]",
              )}
            >
              <input
                ref={(el) => {
                  inputsRef.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                disabled={disabled}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => handleKey(i, e)}
                onPaste={handlePaste}
                onFocus={() => setFocused(i)}
                onBlur={() => setFocused(null)}
                className={cn(
                  "absolute inset-0 w-full h-full bg-transparent text-center",
                  "text-2xl font-semibold tabular-nums tracking-tight",
                  "outline-none caret-accent",
                  error ? "text-danger" : "text-white",
                )}
                autoComplete="one-time-code"
              />
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}
